import React from 'react';
import type { ResultViewType } from '@/constants/scenarioTypes';
import { EvaluationTable } from '@/pages/results/EvaluationTable';
import GcsResult from '@/pages/results/GcsResult';
import PupilResult from '@/pages/results/PupilResult';
import MotorResult from '@/pages/results/MotorResult';
import ProcedureResult from '@/pages/results/ProcedureResult';
import AoEvaluationResult from '@/pages/results/AoEvaluationResult';
import Covid19Result from '@/pages/results/Covid19Result';
import AclsResult from '@/pages/results/AclsResult';
import { TriageSessionPerformanceSection, type TriagePatientRow } from '@/pages/results/TriageResult';
import type { WinnerEntry } from '@/pages/results/Top3Winners';

/** `resolveSessionResultTableView` 결과(ResultViewType)별 본문 메인 블록 */
export interface RenderResultViewProps {
  perfTitle: string;
  resultMetadata: Record<string, unknown> | null | undefined;
  isAllParticipantsView?: boolean;
  winners?: WinnerEntry[];
  /** 시나리오 `content_code` (예: `radiation` → 중증도 확대뷰 ARS 블록) */
  contentCode?: string | null;
  /** `EvaluationTable` result_view — 루브릭·체크리스트 절 제목(데이터 없을 때 스켈레톤 표) */
  rubricBlockTitle: string;
}

/** GCS·동공·근력 표를 한 본문에 나열 */
function AssessmentTables({
  perfTitle,
  resultMetadata,
}: {
  perfTitle: string;
  resultMetadata: Record<string, unknown> | null | undefined;
}): React.ReactElement {
  const scenarioType = resultMetadata?.scenarioType as string | undefined;
  
  // scenario_type에 따라 해당하는 평가 테이블만 렌더링
  if (scenarioType === 'gcs') {
    return <GcsResult title={perfTitle} resultMetadata={resultMetadata} />;
  }
  if (scenarioType === 'pupil_reaction') {
    return <PupilResult title={perfTitle} resultMetadata={resultMetadata} />;
  }
  if (scenarioType === 'motor_power') {
    return <MotorResult title={perfTitle} resultMetadata={resultMetadata} />;
  }
  
  // scenario_type이 없으면 모두 렌더링 (기존 동작 유지)
  return (
    <>
      <GcsResult title={perfTitle} resultMetadata={resultMetadata} />
      <PupilResult title={perfTitle} resultMetadata={resultMetadata} />
      <MotorResult title={perfTitle} resultMetadata={resultMetadata} />
    </>
  );
}

export function renderResultView(
  resultTableView: ResultViewType,
  {
    perfTitle,
    resultMetadata,
    isAllParticipantsView,
    winners,
    contentCode,
    rubricBlockTitle,
  }: RenderResultViewProps,
): React.ReactElement {
  const triagePatients = resultMetadata?.triagePatients;
  const metaContentCode =
    (resultMetadata as { contentCode?: string } | null | undefined)?.contentCode;
  const effectiveContentCode = contentCode ?? metaContentCode ?? null;

  switch (resultTableView) {
    case 'TriageView':
      return (
        <TriageSessionPerformanceSection
          patients={triagePatients as TriagePatientRow[] | undefined}
          isAllParticipantsView={isAllParticipantsView}
          winners={winners}
          contentCode={effectiveContentCode}
        />
      );
    case 'NeurologicalBundleView':
      return <AssessmentTables perfTitle={perfTitle} resultMetadata={resultMetadata} />;
    case 'GcsResult':
      return <GcsResult title={perfTitle} resultMetadata={resultMetadata} />;
    case 'PupilResult':
      return <PupilResult title={perfTitle} resultMetadata={resultMetadata} />;
    case 'MotorResult':
      return <MotorResult title={perfTitle} resultMetadata={resultMetadata} />;
    case 'ProcedureView':
      return (
        <ProcedureResult 
          resultMetadata={resultMetadata} 
          contentCode={effectiveContentCode} 
          isAllParticipantsView={isAllParticipantsView}
        />
      );
    case 'AoEvaluationView':
      return <AoEvaluationResult resultMetadata={resultMetadata} />;
    case 'Covid19View':
      return <Covid19Result resultMetadata={resultMetadata} />;
    case 'AclsView':
      return <AclsResult resultMetadata={resultMetadata} />;
    case 'EvaluationTable':
      /** `EvaluationTable` 전용 — 체크리스트는 ResultBody가 하단·데이터로 조립. 뷰만 `EvaluationTable`인 경우 루브릭 절은 스켈레톤 */
      return (
        <div className="!mt-6 !mb-4">
          <EvaluationTable
            title={rubricBlockTitle}
            type="assessment"
            items={[]}
            expandedFeedback={new Set()}
            onToggleFeedback={() => {}}
          />
        </div>
      );
    default:
      return (
        <>
          <AssessmentTables perfTitle={perfTitle} resultMetadata={resultMetadata} />
          <ProcedureResult 
            resultMetadata={resultMetadata} 
            contentCode={effectiveContentCode} 
            isAllParticipantsView={isAllParticipantsView}
          />
        </>
      );
  }
}
